/**
 * Visual experiments runtime (contract §3) — orchestrates:
 *   early boot  → editor/preview detection, cached config, anti-flicker
 *   after init  → config refresh, URL match, assignment, apply, exposure
 *   navigation  → re-evaluate on SPA route changes
 *
 * Code experiments (integrations calling b2mAssignExperiment themselves) are
 * untouched: only experiments present in the visual config are auto-assigned.
 */
import VisualApplier from './applier';
import { matchesUrlTargets } from './url-match';
import {
  DEFAULT_EDITOR_ORIGINS,
  experimentIdsKey, fetchVisualConfigurations, isCacheFresh, parseExperimentIds, parseOrigins,
  readCachedConfig, resolveConfigUrl, resolveInlineExperiments, writeCachedConfig
} from './config';
import { hideSelectors } from './anti-flicker';
import { installNavigationListener } from './spa';
import { resolvePreview, setPreview, showPreviewBadge, hidePreviewBadge } from './preview';
import EditorBridge, { detectEditorMode } from './editor-bridge';

const lower = v => (v == null ? '' : String(v).toLowerCase());

export default class VisualRuntime {
  constructor(sdk, { scriptSrc = '', nonce = '', win = null } = {}) {
    this.sdk = sdk;
    this.win = win || (typeof window !== 'undefined' ? window : null);
    this.doc = this.win ? this.win.document : null;
    this.scriptSrc = scriptSrc;
    this.nonce = nonce;
    this.logger = sdk && sdk.logger ? sdk.logger : { log() {}, warn() {}, error() {} };

    this.mode = 'idle';            // idle | live | preview | editor | disabled
    this.settings = null;
    this.experiments = [];
    this.source = 'none';          // inline | cache | remote | none
    this.matchedIds = [];
    this.previewState = null;
    this.exposures = {};
    this.hide = null;
    this.bridge = null;
    this.applier = null;
    this.uninstallNav = null;
    this._booted = false;
    this._afterInitDone = false;
    this._evaluating = null;
    this.ready = new Promise(resolve => { this._resolveReady = resolve; });
  }

  // ── settings ──────────────────────────────────────────────────────────

  _scriptTag() {
    const doc = this.doc;
    if (!doc) return null;
    const cur = doc.currentScript;
    if (cur && cur.hasAttribute && cur.hasAttribute('data-api-key')) return cur;
    try { return doc.querySelector('script[data-api-key]'); } catch (e) { return null; }
  }

  /** Settings available at script evaluation time (before Config.init). */
  _readEarlySettings() {
    const tag = this._scriptTag();
    const attr = name => (tag && tag.hasAttribute(name) ? tag.getAttribute(name) : null);
    const g = (this.win && this.win.b2mConfig && typeof this.win.b2mConfig === 'object') ? this.win.b2mConfig : {};
    const attrInline = attr('data-visual-experiments');
    const inline = attrInline != null ? resolveInlineExperiments(attrInline) : resolveInlineExperiments(g.visualExperiments);
    const origins = parseOrigins(attr('data-visual-editor-origins') || g.visualEditorOrigins);
    const configUrl = attr('data-visual-config-url') || g.visualConfigUrl || null;
    return {
      disabled: inline === false,
      inline: Array.isArray(inline) ? inline : null,
      configUrl: resolveConfigUrl({ configUrl, experimentApiUrl: g.experimentApiUrl }),
      rawConfigUrl: configUrl,
      experimentIds: parseExperimentIds(attr('data-visual-experiment-ids') || g.visualExperimentIds),
      origins: origins.length ? origins : DEFAULT_EDITOR_ORIGINS.slice(),
      apiKey: attr('data-api-key') || g.apiKey || null,
      editorUrl: attr('data-visual-editor-url') || g.visualEditorUrl || null
    };
  }

  /** Final settings from the merged SDK config (after Config.init). */
  _readFinalSettings() {
    const c = this.sdk && this.sdk.config ? this.sdk.config : {};
    const early = this.settings || this._readEarlySettings();
    const inline = resolveInlineExperiments(c.visualExperiments);
    const origins = parseOrigins(c.visualEditorOrigins);
    const ids = parseExperimentIds(c.visualExperimentIds);
    return {
      disabled: inline === false || early.disabled,
      inline: Array.isArray(inline) ? inline : early.inline,
      configUrl: resolveConfigUrl({ configUrl: c.visualConfigUrl || early.rawConfigUrl, experimentApiUrl: c.experimentApiUrl }),
      rawConfigUrl: c.visualConfigUrl || early.rawConfigUrl,
      experimentIds: ids.length ? ids : early.experimentIds,
      origins: origins.length ? origins : early.origins,
      apiKey: c.apiKey || early.apiKey || null,
      editorUrl: c.visualEditorUrl || early.editorUrl || null
    };
  }

  _track(name, params) {
    try {
      const b2m = this.win && this.win.b2m;
      if (b2m && typeof b2m.track === 'function') b2m.track(name, params);
    } catch (e) { /* telemetry must never break the page */ }
  }

  _ensureApplier() {
    if (!this.applier) {
      this.applier = new VisualApplier({
        win: this.win, doc: this.doc, logger: this.logger, nonce: this.nonce,
        track: (n, p) => this._track(n, p)
      });
    }
    return this.applier;
  }

  // ── early boot ────────────────────────────────────────────────────────

  earlyBoot() {
    if (this._booted || !this.win || !this.doc) return;
    this._booted = true;
    this.settings = this._readEarlySettings();
    if (this.settings.disabled) { this.mode = 'disabled'; return; }

    const editorMode = detectEditorMode(this.win);
    if (editorMode) {
      this.mode = 'editor';
      this.bridge = new EditorBridge({
        win: this.win, logger: this.logger, sdk: this.sdk, sdkScriptSrc: this.scriptSrc,
        nonce: this.nonce, editorUrl: this.settings.editorUrl,
        getOrigins: () => (this.settings ? this.settings.origins : DEFAULT_EDITOR_ORIGINS)
      });
      this.bridge.start(editorMode);
      return;
    }

    this.previewState = resolvePreview(this.win);
    this._loadExperimentsSync();
    if (this.experiments.length === 0) return;

    const href = this.win.location.href;
    const matched = this.experiments.filter(e => matchesUrlTargets(e.urlTargets, href, e.editorUrl));
    this.matchedIds = matched.map(e => e.experimentId);
    if (matched.length === 0) return;

    if (this.previewState) {
      this.mode = 'preview';
      this._applyPreview(matched);
      return;
    }
    const selectors = [];
    let timeoutMs = 1200;
    matched.forEach(e => {
      if (e.settings && e.settings.antiFlicker && e.settings.antiFlicker.mode === 'off') return;
      if (e.settings && e.settings.antiFlicker) timeoutMs = Math.min(timeoutMs, e.settings.antiFlicker.timeoutMs);
      e.variants.forEach(v => v.changes.forEach(c => { if (c.enabled !== false) selectors.push(c.selector); }));
    });
    if (selectors.length) {
      this.hide = hideSelectors({ doc: this.doc, selectors, timeoutMs, nonce: this.nonce, logger: this.logger });
    }
  }

  /**
   * Inline config wins; otherwise the cached remote config (sync), limited to
   * the experiment ids the site lists today.
   */
  _loadExperimentsSync() {
    const s = this.settings;
    if (s.inline) { this.experiments = s.inline; this.source = 'inline'; return; }
    const cache = readCachedConfig(this.sdk && this.sdk.storage, this.win);
    const ids = new Set(s.experimentIds || []);
    if (cache && ids.size && (!s.apiKey || !cache.apiKey || cache.apiKey === s.apiKey)) {
      this.experiments = cache.experiments.filter(e => ids.has(e.experimentId));
      this.source = 'cache';
      this._cache = cache;
    } else {
      this.experiments = [];
      this.source = 'none';
    }
    this._withPreviewExperiment();
  }

  /** A previewed experiment fetched on demand stays in the list (never cached). */
  _withPreviewExperiment() {
    const extra = this._previewExperiment;
    if (extra && !this.experiments.some(e => e.experimentId === extra.experimentId)) {
      this.experiments = this.experiments.concat(extra);
    }
  }

  _fetchImpl() {
    return this.win && this.win.fetch ? this.win.fetch.bind(this.win) : null;
  }

  /**
   * `?b2m_preview=<experimentId>:<groupId>` for an experiment the site does
   * not list (a draft, or ids not configured yet): read its snapshot once.
   */
  async _ensurePreviewExperiment() {
    const id = this.previewState && this.previewState.experimentId;
    if (!id || this.experiments.some(e => e.experimentId === id)) return;
    if (this._previewFetchedId === id) return;
    this._previewFetchedId = id;
    const s = this.settings;
    if (!s || !s.configUrl) return;
    const [result] = await fetchVisualConfigurations({ url: s.configUrl, experimentIds: [id], fetchImpl: this._fetchImpl() });
    if (result && result.status === 'ok') {
      this._previewExperiment = result.changeset;
      this._withPreviewExperiment();
    } else {
      this.logger.warn('[B2M Visual] preview experiment has no visual configuration:', id);
    }
  }

  _reveal() {
    if (this.hide) { this.hide.reveal(); this.hide = null; }
  }

  // ── after init ────────────────────────────────────────────────────────

  afterInit() {
    if (this._afterInitDone) return this.ready;
    this._afterInitDone = true;
    try {
      this.settings = this._readFinalSettings();
      if (this.settings.disabled) {
        this.mode = 'disabled';
        this._reveal();
        this._resolveReady();
        return this.ready;
      }
      if (this.mode === 'editor') {
        this._resolveReady();
        return this.ready;
      }
      this._loadExperimentsSync();
      if (!this.uninstallNav) {
        this.uninstallNav = installNavigationListener(this.win, () => { this.evaluate('navigation'); });
      }
      const first = this.evaluate('init');
      first.then(() => this._resolveReady(), () => this._resolveReady());
      if (!this.settings.inline) {
        first.then(() => this._refreshRemote()).then(changed => { if (changed) this.evaluate('config'); }).catch(() => {});
      }
    } catch (e) {
      this.logger.error('[B2M Visual] afterInit failed:', e);
      this._reveal();
      this._resolveReady();
    }
    return this.ready;
  }

  /**
   * Re-read the snapshots of the listed experiments from experiments-api
   * (at most every VISUAL_CACHE_TTL_MS, or when the id list changed). An
   * experiment whose request failed keeps its cached configuration.
   */
  async _refreshRemote() {
    const s = this.settings;
    const ids = s && Array.isArray(s.experimentIds) ? s.experimentIds : [];
    if (!s || !s.configUrl || ids.length === 0) return false;
    const idsKey = experimentIdsKey(ids);
    const cache = this._cache || readCachedConfig(this.sdk && this.sdk.storage, this.win);
    const sameScope = cache && (!s.apiKey || cache.apiKey === s.apiKey);
    if (sameScope && cache.ids === idsKey && isCacheFresh(cache)) return false;
    const results = await fetchVisualConfigurations({ url: s.configUrl, experimentIds: ids, fetchImpl: this._fetchImpl() });
    if (results.length && results.every(r => r.status === 'error')) {
      this.logger.warn('[B2M Visual] visual configurations unavailable, serving cache');
      return false;
    }
    const cached = new Map((sameScope ? cache.experiments : []).map(e => [e.experimentId, e]));
    const experiments = [];
    results.forEach(r => {
      if (r.status === 'ok') experiments.push(r.changeset);
      else if (r.status === 'error' && cached.has(r.experimentId)) experiments.push(cached.get(r.experimentId));
    });
    writeCachedConfig(this.sdk && this.sdk.storage, { experiments, apiKey: s.apiKey, ids: idsKey }, this.win);
    const before = JSON.stringify(this.experiments);
    this.experiments = experiments;
    this._withPreviewExperiment();
    this.source = 'remote';
    this._cache = null;
    return before !== JSON.stringify(this.experiments);
  }

  // ── evaluation ────────────────────────────────────────────────────────

  evaluate(reason = 'manual') {
    if (this._evaluating) {
      this._queued = reason;
      return this._evaluating;
    }
    this._evaluating = this._evaluate(reason).catch(e => {
      this.logger.error('[B2M Visual] evaluate failed:', e);
    }).then(() => {
      this._evaluating = null;
      this._reveal();
      if (this._queued) { const r = this._queued; this._queued = null; return this.evaluate(r); }
      return undefined;
    });
    return this._evaluating;
  }

  async _evaluate(reason) {
    if (this.mode === 'editor' || this.mode === 'disabled') return;
    if (this.previewState) await this._ensurePreviewExperiment();
    const href = this.win.location.href;
    const matched = this.experiments.filter(e => matchesUrlTargets(e.urlTargets, href, e.editorUrl));
    this.matchedIds = matched.map(e => e.experimentId);
    const applier = this._ensureApplier();

    // Revert what no longer matches this URL.
    const matchedSet = new Set(this.matchedIds);
    applier.status().applied.forEach(a => { if (!matchedSet.has(a.experimentId)) applier.revert(a.experimentId); });

    if (this.previewState) {
      this.mode = 'preview';
      this._applyPreview(matched);
      return;
    }
    if (matched.length === 0) { this.mode = this.experiments.length ? 'live' : 'idle'; return; }
    this.mode = 'live';

    const ids = matched.map(e => e.experimentId);
    let assignments = [];
    try {
      assignments = await this.sdk.experiments.assign(ids, {}, { deferExposure: true });
    } catch (e) {
      this.logger.warn('[B2M Visual] assignment failed:', e);
    }
    const toExpose = [];
    matched.forEach(exp => {
      const a = assignments.find(x => x && lower(x.ExperimentId) === exp.experimentId);
      if (!a) return;
      const variant = exp.variants.find(v => v.groupId === lower(a.GroupId));
      if (variant && variant.changes.length) {
        applier.apply(exp.experimentId, variant, exp.settings, { groupId: variant.groupId });
      } else {
        applier.revert(exp.experimentId);
      }
      toExpose.push(a);
    });
    if (reason === 'navigation' || reason === 'manual') applier.reapplyAll();
    this._reveal();
    if (toExpose.length) {
      const exposed = this.sdk.experiments.trackExposure(toExpose, { experiment_type: 'visual' });
      const now = Date.now();
      (exposed || []).forEach(id => { this.exposures[lower(id)] = now; });
    }
  }

  _applyPreview(matched) {
    const p = this.previewState;
    const exp = (matched || this.experiments).find(e => e.experimentId === p.experimentId);
    const applier = this._ensureApplier();
    if (!exp) { this.logger.warn('[B2M Visual] preview experiment not in config:', p.experimentId); return; }
    const variant = exp.variants.find(v => v.groupId === p.groupId);
    if (!variant) { this.logger.warn('[B2M Visual] preview group not in changeset:', p.groupId); return; }
    applier.apply(exp.experimentId, variant, exp.settings, { groupId: variant.groupId });
    this._schedulePreviewStatus(exp, variant);
    const show = () => showPreviewBadge(this.doc);
    if (this.doc.body) show();
    else this.doc.addEventListener('DOMContentLoaded', show, { once: true });
  }

  /**
   * Preview inside the panel (an iframe): tell the parent, change by change,
   * whether it was applied. Posted only to the allow-listed editor origins,
   * a few times while late elements may still appear.
   */
  _schedulePreviewStatus(exp, variant) {
    const win = this.win;
    let parent = null;
    try { parent = win.parent && win.parent !== win ? win.parent : null; } catch (e) { parent = null; }
    if (!parent) return;
    const startedAt = Date.now();
    const waitMs = Number(exp.settings && exp.settings.waitForElementMs) || 5000;
    const post = () => {
      if (!this.previewState || this.previewState.experimentId !== exp.experimentId) return;
      const st = this.applier ? this.applier.status() : { pending: [], tripped: [] };
      const pending = new Set(st.pending || []);
      const tripped = new Set(st.tripped || []);
      const elapsed = Date.now() - startedAt;
      const changes = variant.changes.map(c => {
        if (c.enabled === false) return { id: c.id, status: 'disabled' };
        const key = `${exp.experimentId}::${c.id}`;
        if (tripped.has(key)) return { id: c.id, status: 'conflict' };
        if (pending.has(key)) return { id: c.id, status: elapsed > waitMs ? 'missing' : 'waiting' };
        if (c.action === 'style' || c.action === 'hide' || c.action === 'image') {
          let found = false;
          try { found = !!this.doc.querySelector(c.selector); } catch (e) { found = false; }
          if (!found) return { id: c.id, status: elapsed > waitMs ? 'missing' : 'waiting' };
        }
        return { id: c.id, status: 'applied' };
      });
      const message = {
        type: 'b2m-ve:preview-status', v: 1,
        experimentId: exp.experimentId, groupId: variant.groupId, changes
      };
      const origins = (this.settings && this.settings.origins) || DEFAULT_EDITOR_ORIGINS;
      origins.forEach(origin => {
        try { parent.postMessage(message, origin); } catch (e) { /* not that origin */ }
      });
    };
    [300, 1500, waitMs + 300].forEach(ms => setTimeout(post, ms));
  }

  // ── debug api ─────────────────────────────────────────────────────────

  status() {
    return {
      mode: this.mode,
      source: this.source,
      experiments: this.experiments.map(e => e.experimentId),
      experimentIds: this.settings && this.settings.experimentIds ? this.settings.experimentIds.slice() : [],
      matched: this.matchedIds.slice(),
      preview: this.previewState,
      exposures: { ...this.exposures },
      applier: this.applier ? this.applier.status() : { applied: [], pending: [], tripped: [], cssRules: 0 },
      editorSession: this.bridge && this.bridge.session ? { nonce: this.bridge.session.nonce, trustedOrigin: this.bridge.session.trustedOrigin } : null
    };
  }

  reapply() {
    return this.evaluate('manual');
  }

  revert() {
    if (this.applier) this.applier.revertAll();
    if (this.previewState) { this.previewState = null; setPreview(this.win, null, null); hidePreviewBadge(this.doc); }
    return this.status();
  }

  preview(experimentId, groupId) {
    if (!experimentId || !groupId) {
      this.previewState = null;
      setPreview(this.win, null, null);
      hidePreviewBadge(this.doc);
      if (this.applier) this.applier.revertAll();
    } else {
      this.previewState = setPreview(this.win, experimentId, groupId);
      if (this.applier) this.applier.revertAll();
    }
    return this.evaluate('manual');
  }
}
